<template>
  <ag-index-page :view="view" :columns="columns" />
</template>

<script setup lang="ts">
import type { ThinCertificate } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { mode2FALabel, stateLabel } from "@/utils/labels";

/**
 * Certificados propios.
 *
 * Sin borrado: MyCertificateController no expone DELETE. La vista anterior tenía
 * un botón "Eliminar certificado" fijo en `disabled`, que era la forma más
 * confusa posible de decir que la operación no existe.
 *
 * Los filtros son estrictamente menos que en el índice de administración: este
 * endpoint no acepta user_id, user_username, person_dni ni twofa_mode. La vista
 * anterior ofrecía "ID usuario", que el endpoint ignoraba.
 */
const view = useIndexView<ThinCertificate, "fingerprint" | "key_alias" | "key_id" | "csr_id">({
  title: "Mis certificados",
  rowKey: "fingerprint",
  defaultSort: "fingerprint,asc",
  fetchPage: (params) => banco.user.certificates.find(params),
  filters: [
    { field: "fingerprint", label: "Fingerprint" },
    { field: "key_alias", label: "Alias llave" },
    { field: "key_id", label: "ID llave" },
    { field: "csr_id", label: "ID CSR" },
  ],
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "Fingerprint", value: "fingerprint", class: "text-no-wrap" },
    row: {
      type: "LINK",
      to: "/my/certificates/{fingerprint}/edit",
      transform: (val) => `${String(val).slice(0, 16)}…`,
    },
  },
  {
    header: { text: "Estado", value: "state", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { ACTIVE: "success", INACTIVE: "error", DELETED: "grey" },
      transform: (val) => stateLabel(val),
    },
  },
  {
    header: { text: "2FA", value: "twofa_mode", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { NONE: "grey", TOTP: "indigo", RSAOTP: "indigo", SMS: "blue", EMAIL: "blue" },
      transform: (val) => mode2FALabel(val),
    },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Ver detalle", icon: "mdi-eye", link: "/my/certificates/{fingerprint}/edit" },
      ],
    },
  },
];
</script>
